import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import {
  useWorklistStore,
  useWorklistSession,
  WorklistFilter,
  SavedFilter,
  DATE_SPAN_OPTIONS,
  savedFilterToWorklist,
} from '../stores/worklistStore';
import { apiCall, getServerUrl } from '../api/client';
import { sendToBurn, getEdgePrintInstallerUrl, launchCompanion } from '../utils/composerHandoff';
import CompanionLaunchDialog from '../components/CompanionLaunchDialog';
import { SeriesData } from '../cornerstone/init';
import SendDialog from '../components/SendDialog';
import JobsPanel from '../components/JobsPanel';
import ReportBadge from '../components/ReportBadge';
import {
  WorklistPluginColumn,
  useWorklistViewTrigger,
  ManualBulkSendButton,
  studyRowToFacts,
} from '../plugins/runtime/host/WorklistIntegration';
import { usePluginsForSlot } from '../plugins/runtime/hooks';
import SettingsDialog from '../components/SettingsDialog';
import { sendAddStudy } from '../utils/viewerChannel';
import { loadUserSettings, DEFAULT_VIEWER_OPEN_MODE } from '../utils/userSettings';
import { subscribeToReportChanges } from '../utils/worklistEvents';
import { getProvider, refreshProvider } from '../data/providers';
import Viewer from '../components/Viewer';
import styles from './WorklistPage.module.css';

interface Study {
  studyId: number;
  studyInstanceUID: string;
  patientName: string;
  patientId: string;
  patientSex: string;
  patientAge: string;
  accessionNumber: string;
  studyDate: string;
  modality: string;
  studyDescription: string;
  referringPhysician: string;
  institution: string;
  department: string;
  readingPhysician: string;
  performingPhysician: string;
  operator: string;
  numImages: number;
  advStatus: string;
  /** 'none' | 'draft' | 'signed' — included when backend has LEFT JOIN on img_reports */
  reportStatus?: 'none' | 'draft' | 'signed' | string;
}

export default function WorklistPage() {
  const navigate = useNavigate();
  const { displayName, userId, logout } = useAuthStore();
  const currentUsername = useAuthStore((s) => s.username);
  // Same gate as the composer buttons: preview-only for the dev account
  // until the desktop burn engine ships. Case-insensitive.
  const canBurnMedia = currentUsername?.toLowerCase() === 'sriram';

  function handleBurnSelected() {
    if (selectedStudyIds.size === 0) return;
    const ids = [...selectedStudyIds];
    const acctoken = useAuthStore.getState().acctoken;
    fireDeepLink(
      ids.length === 1
        ? `Burning study ${ids[0]}`
        : `Burning ${ids.length} studies`,
      () => sendToBurn(ids, getServerUrl(), acctoken),
    );
  }

  // Held so the "Retry launch" button in the install dialog can re-fire
  // exactly the same action the user originally clicked.
  const [lastLaunchAction, setLastLaunchAction] = useState<(() => boolean) | null>(null);

  function handleOpenSelected() {
    if (selectedStudyIds.size === 0) return;
    // Open first selected in the primary viewer; extras go into new tabs so
    // the user can compare studies side-by-side. Same routing rules
    // openStudy() applies (modal / multi-tab / separate).
    const ids = [...selectedStudyIds];
    openStudy(ids[0]);
    for (let i = 1; i < ids.length; i++) {
      window.open(viewerUrl(ids[i]), '_blank');
    }
  }

  function handleSendSelected() {
    // Send accepts exactly one study — the toolbar button enforces this
    // via `disabled={selectedCount !== 1}`. This function is defensive:
    // if we somehow got called with 0 or >1, drop the click.
    if (selectedStudyIds.size !== 1) return;
    const id = [...selectedStudyIds][0];
    const study = studies.find((s) => s.studyId === id);
    if (study) setSendStudy(study);
  }
  const {
    filters, columns, savedFilters,
    setFilters, setColumns, resetColumns, setSavedFilters,
  } = useWorklistStore();

  const { studies, setStudies, studiesFetchedAt } = useWorklistSession() as {
    studies: Study[];
    setStudies: (s: Study[]) => void;
    studiesFetchedAt: number;
  };
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Local search — filters the already-loaded worklist client-side
  const [localSearch, setLocalSearch] = useState('');

  // Expanded study series
  const [expandedStudyId, setExpandedStudyId] = useState<number | null>(null);
  const [expandedSeries, setExpandedSeries] = useState<SeriesData[]>([]);
  const [loadingSeries, setLoadingSeries] = useState(false);

  // Multi-select — separate from "expanded" (which controls the series drawer).
  // The toolbar Open / Send / Push / Burn actions all operate on this set so
  // a user can hold Shift/Ctrl to build up a selection then act on it once.
  const [selectedStudyIds, setSelectedStudyIds] = useState<Set<number>>(new Set());
  const selectedCount = selectedStudyIds.size;
  const firstSelected = selectedCount > 0 ? [...selectedStudyIds][0] : null;

  function toggleSelected(id: number) {
    setSelectedStudyIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  /** Select-all operates on `displayedStudies` (what the user actually
   *  sees after the local search), not the full `studies` array. If every
   *  visible row is already selected, clear the selection; otherwise add
   *  all visible rows to it. */
  function toggleSelectAll() {
    setSelectedStudyIds((prev) => {
      const visibleIds = displayedStudies.map((s) => s.studyId);
      const allChecked = visibleIds.length > 0 && visibleIds.every((id) => prev.has(id));
      if (allChecked) {
        const next = new Set(prev);
        visibleIds.forEach((id) => next.delete(id));
        return next;
      }
      const next = new Set(prev);
      visibleIds.forEach((id) => next.add(id));
      return next;
    });
  }

  // Install-dialog state. Set ONLY when launchCompanion() detects the
  // protocol handler didn't take focus within its timeout window - i.e.
  // the Companion isn't installed on this machine. On machines where the
  // Companion IS installed, nothing shows: the deep link fires, the OS
  // hands off, and the user's action just happens.
  const [launchingMsg, setLaunchingMsg] = useState<string | null>(null);

  async function fireDeepLink(msg: string, fire: () => boolean) {
    setLastLaunchAction(() => fire);
    const outcome = await launchCompanion(fire);
    if (outcome === 'not_installed') {
      setLaunchingMsg(msg);
    }
    // outcome === 'launched': the Companion took focus, nothing to do.
  }

  // Send dialog
  const [sendStudy, setSendStudy] = useState<Study | null>(null);
  /** Modal viewer state — non-null when viewerOpenMode='modal' AND a study
   *  has been opened. The Viewer component is rendered as an overlay over
   *  the worklist; closing it just clears this state, leaving worklist
   *  position / scroll / search untouched. */
  const [modalViewer, setModalViewer] = useState<{ studyId: number; seriesId?: number } | null>(null);
  const [jobRefreshTrigger, setJobRefreshTrigger] = useState(0);
  // Initial state honors a sessionStorage flag set by SettingsDialog when
  // it navigates to the template editor. This is how the Back button on
  // TemplatePage lands the user back on the Templates tab of Settings
  // instead of just on the worklist. The flag is cleared as soon as we
  // consume it so a normal refresh doesn't pop Settings unexpectedly.
  const [showSettings, setShowSettings] = useState<boolean>(() => {
    try {
      const flag = sessionStorage.getItem('reopenSettingsOnTab');
      if (flag) {
        sessionStorage.removeItem('reopenSettingsOnTab');
        // Stash the wanted tab where SettingsDialog can read it.
        try { sessionStorage.setItem('settingsInitialTab', flag); } catch { /* ignore */ }
        return true;
      }
    } catch { /* private mode */ }
    return false;
  });

  // Custom Search dialog
  const [showCustomSearch, setShowCustomSearch] = useState(false);
  const [draft, setDraft] = useState<WorklistFilter>(filters);

  // Column config
  const [showColumnConfig, setShowColumnConfig] = useState(false);
  const columnConfigRef = useRef<HTMLDivElement>(null);
  const columnsBtnRef = useRef<HTMLButtonElement>(null);
  const [dragColKey, setDragColKey] = useState<string | null>(null);
  const resizingCol = useRef<{ key: string; startX: number; startW: number } | null>(null);

  // Column sort. Click a header to toggle asc -> desc -> off. Suppressed
  // during a drag-reorder or resize (those gestures stopPropagation on
  // their respective handlers).
  const [sort, setSort] = useState<{ col: string; dir: 'asc' | 'desc' } | null>(null);
  function cycleSort(colKey: string) {
    setSort((cur) => {
      if (!cur || cur.col !== colKey) return { col: colKey, dir: 'asc' };
      if (cur.dir === 'asc') return { col: colKey, dir: 'desc' };
      return null; // asc -> desc -> off
    });
  }

  // Esc closes the modal viewer (modal mode only). Bound at the worklist
  // level since Viewer itself doesn't close on Esc — the modal shell owns
  // the dismiss gesture.
  useEffect(() => {
    if (!modalViewer) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        // Don't close if the user is typing inside an input within the modal
        const t = e.target as HTMLElement | null;
        if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA')) return;
        setModalViewer(null);
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [modalViewer]);

  // Close column dropdown when clicking outside (but not on the toggle button itself)
  useEffect(() => {
    if (!showColumnConfig) return;
    function onDown(e: MouseEvent) {
      const t = e.target as Node;
      if (columnConfigRef.current?.contains(t)) return;
      if (columnsBtnRef.current?.contains(t)) return;
      setShowColumnConfig(false);
    }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [showColumnConfig]);

  // Filter save/edit dialog
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [editingFilter, setEditingFilter] = useState<SavedFilter | null>(null);
  const [filterName, setFilterName] = useState('');
  const [filterIsDefault, setFilterIsDefault] = useState(false);
  const [activeFilterId, setActiveFilterId] = useState<number | null>(null);
  const [showManageFilters, setShowManageFilters] = useState(false);

  // Load saved filters on mount. Skip if already cached — savedFilters is
  // persisted via zustand-persist, so on back-from-viewer we already have it.
  useEffect(() => {
    document.title = 'Worklist';
    if (savedFilters.length === 0) loadFilters();
  }, []);

  // Auto-apply default filter ONLY on a true first-load. If studies are still
  // in the in-memory session store (e.g. user just came back from the viewer),
  // skip — otherwise every Back-button trip triggers a fresh server search.
  useEffect(() => {
    if (savedFilters.length === 0) return;
    if (studies.length > 0) return;
    const defaultFilter = savedFilters.find((f) => f.isDefault);
    if (defaultFilter) {
      applySavedFilter(defaultFilter);
    }
  }, [savedFilters.length > 0 ? savedFilters[0]?.filterId : 0]);

  async function loadFilters() {
    const res = await apiCall<SavedFilter>('filter.list', { userId: userId || 0 });
    if (res.s === 'ok') {
      setSavedFilters(res.data || []);
    }
  }

  // Server search — sends filters to the server
  const search = useCallback(async (f?: WorklistFilter) => {
    const searchFilters = { ...(f || filters) };

    // Resolve date span to actual from/to dates before sending
    if (searchFilters.studyDateSpan && searchFilters.studyDateSpan > 0 && searchFilters.studyDateSpan !== 254) {
      const today = new Date();
      let from = new Date();
      const to = new Date();

      switch (searchFilters.studyDateSpan) {
        case 1: break; // Today — from = today
        case 2: from.setDate(today.getDate() - 1); to.setDate(today.getDate() - 1); break;
        case 3: from.setDate(today.getDate() - 2); to.setDate(today.getDate() - 2); break;
        case 4: from.setDate(today.getDate() - 7); break;
        case 5: from.setDate(today.getDate() - 14); break;
        case 6: from.setMonth(today.getMonth() - 1); break;
        case 7: from.setMonth(today.getMonth() - 3); break;
        case 8: from.setMonth(today.getMonth() - 6); break;
        case 9: from.setFullYear(today.getFullYear() - 1); break;
      }

      const fmt = (d: Date) => d.toISOString().slice(0, 10).replace(/-/g, ''); // yyyymmdd
      searchFilters.studyDateFrom = fmt(from);
      searchFilters.studyDateTo = fmt(to);
    }

    // Don't send the span code itself — server expects from/to
    delete searchFilters.studyDateSpan;

    // Convert manual date inputs (yyyy-mm-dd from HTML) to yyyymmdd
    if (searchFilters.studyDateFrom && searchFilters.studyDateFrom.includes('-')) {
      searchFilters.studyDateFrom = searchFilters.studyDateFrom.replace(/-/g, '');
    }
    if (searchFilters.studyDateTo && searchFilters.studyDateTo.includes('-')) {
      searchFilters.studyDateTo = searchFilters.studyDateTo.replace(/-/g, '');
    }

    setLoading(true);
    setError('');
    setLocalSearch('');
    try {
      // Await refresh first — getProvider() is synchronous and reads a
      // module-level cache that starts as the EDGEPACS default. Without
      // this await, the very first search after login (which fires from
      // the default-filter auto-apply effect) would use the wrong
      // provider until the background settings load completes.
      await refreshProvider();
      const provider = getProvider();
      // Diagnostic: log the exact payload we send so it's obvious in the
      // browser console when a search returns 0 rows unexpectedly. Users
      // can copy this line and share for backend triage.
      console.info('[Worklist] searchStudies payload', searchFilters);
      const data = await provider.searchStudies(searchFilters as any);
      console.info('[Worklist] searchStudies returned', data.length, 'rows');
      setStudies(data as Study[]);
    } catch (e: any) {
      setError(e?.message || 'Unable to reach the server. Please check your connection.');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  // Refresh the worklist whenever a report status changes elsewhere
  // (delete draft, sign, save first draft). Fires both cross-tab (storage
  // event) and same-tab (CustomEvent). We re-run the CURRENT search so
  // the user's filter stays applied; if nothing has been searched yet
  // (empty studies), we skip - nothing to refresh.
  useEffect(() => {
    return subscribeToReportChanges(() => {
      if (studies.length === 0) return;
      void search();
    });
  }, [search, studies.length]);

  // Mount-time catch-up refresh: if a report status change fired while
  // this page was unmounted (typical: user opened /report/N, deleted the
  // draft, navigated back), the CustomEvent listener above never got the
  // signal - it wasn't mounted. We compare the last-refresh timestamp
  // (written to localStorage by broadcastReportChanged) against when the
  // session-cached studies were last fetched. If the signal is newer,
  // the cached list is stale so refresh silently. No refresh if there
  // are no studies yet - the default-filter effect handles first load.
  useEffect(() => {
    if (studies.length === 0 || studiesFetchedAt === 0) return;
    try {
      const raw = localStorage.getItem('edgeweb-worklist-refresh');
      const signaledAt = raw ? Number(raw) : 0;
      if (signaledAt > studiesFetchedAt) {
        void search();
      }
    } catch { /* private mode */ }
    // Deliberately only on mount - remounts are what miss the live signal.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // No standalone refreshProvider() effect here — search() awaits it
  // inline, so the right provider is always used for the first query.

  // Apply custom search form
  function applyCustomSearch() {
    setFilters(draft);
    setActiveFilterId(null);
    setShowCustomSearch(false);
    search(draft);
  }

  function clearFilters() {
    const empty: WorklistFilter = {};
    setDraft(empty);
    setFilters(empty);
    setActiveFilterId(null);
    setStudies([]);
    setLocalSearch('');
  }

  function applySavedFilter(sf: SavedFilter) {
    const wf = savedFilterToWorklist(sf);
    setDraft(wf);
    setFilters(wf);
    setActiveFilterId(sf.filterId);
    search(wf);
  }

  // Filter save/edit
  function openSaveDialog(existing?: SavedFilter) {
    if (existing) {
      setEditingFilter(existing);
      setFilterName(existing.name);
      setFilterIsDefault(existing.isDefault);
      // Populate draft with the filter's criteria so all fields are editable
      setDraft(savedFilterToWorklist(existing));
    } else {
      setEditingFilter(null);
      setFilterName('');
      setFilterIsDefault(false);
      // draft keeps current search criteria for "Save As"
    }
    setShowSaveDialog(true);
  }

  async function handleSaveFilter() {
    if (!filterName.trim()) return;
    const payload = {
      userId: userId || 0,
      filterId: editingFilter?.filterId || 0,
      name: filterName,
      description: '',
      patientName: draft.patientName || '',
      patientId: draft.patientId || '',
      studyDescription: draft.studyDescription || '',
      patientSex: draft.patientSex || '',
      modality: draft.modality || '',
      accessionNumber: draft.accessionNumber || '',
      studyDateSpan: draft.studyDateSpan || 0,
      queryType: '',
      isDefault: filterIsDefault,
      institution: draft.institution || '',
      referringPhysician: draft.referringPhysician || '',
    };
    const res = await apiCall('filter.save', payload);
    if (res.s === 'ok') {
      setShowSaveDialog(false);
      loadFilters();
    }
  }

  async function handleDeleteFilter(filterId: number) {
    if (!confirm('Delete this filter?')) return;
    await apiCall('filter.delete', { userId: userId || 0, filterId });
    if (activeFilterId === filterId) setActiveFilterId(null);
    loadFilters();
  }

  /** Build a viewer URL — uses HashRouter convention so the new tab boots
   *  the SPA at the right route. Auth comes along via localStorage.
   *
   *  Derives the base from the current URL rather than `import.meta.env.BASE_URL`
   *  because Vite's `base: './'` (needed so the built dist works at any sub-path)
   *  makes BASE_URL `'./'`, which concatenated to `window.location.origin`
   *  produces `http://server./` — an invalid hostname with a trailing dot.
   *  Same pattern as getServerUrl() in src/api/client.ts. */
  function viewerUrl(studyId: number, seriesId?: number): string {
    const origin = window.location.origin;
    const path = window.location.pathname
      .replace(/\/+$/, '')
      .replace(/\/[^/]*\.html?$/i, '');
    const route = seriesId
      ? `viewer/${studyId}?series=${seriesId}`
      : `viewer/${studyId}`;
    return `${origin}${path}/#/${route}`;
  }

  /**
   * Open a study according to the user's `viewerOpenMode` setting.
   *
   * - 'multi-tab' (default): broadcast to any open viewer tab; if it acks,
   *    the study lands there. If silent (~250ms), open a fresh tab — that
   *    tab will become the multi-study viewer for subsequent opens.
   * - 'separate-tab': always open a new tab (legacy behavior).
   *
   * Series-scoped opens (?series=) always use a fresh tab so the requested
   * series controls the viewer's initial state — broadcasting a series
   * request into a tab that's already showing other studies would confuse
   * the user.
   */
  async function openStudy(studyId: number) {
    // Force-refresh settings — using the in-memory cache here meant a fresh
    // "Save" in the Settings dialog didn't always show up in time, so
    // viewerOpenMode='modal' silently fell back to the cached default. The
    // extra round-trip is negligible vs. the cost of the user's click.
    const settings = await loadUserSettings(true).catch(() => ({} as any));
    const mode = (settings.viewerOpenMode as string) || DEFAULT_VIEWER_OPEN_MODE;
    console.log(`[openStudy] viewerOpenMode resolved to "${mode}" (modalViewer=${!!modalViewer})`);
    if (mode === 'modal') {
      if (modalViewer) {
        const ackd = await sendAddStudy(studyId);
        if (ackd) return;
      }
      setModalViewer({ studyId });
      return;
    }
    if (mode === 'multi-tab') {
      const ackd = await sendAddStudy(studyId);
      if (ackd) return;
    }
    window.open(viewerUrl(studyId), '_blank');
  }

  async function openStudyWithSeries(studyId: number, seriesId: number) {
    const settings = await loadUserSettings(true).catch(() => ({} as any));
    const mode = (settings.viewerOpenMode as string) || DEFAULT_VIEWER_OPEN_MODE;
    console.log(`[openStudyWithSeries] viewerOpenMode resolved to "${mode}"`);
    if (mode === 'modal') {
      if (modalViewer) {
        const ackd = await sendAddStudy(studyId, seriesId);
        if (ackd) return;
      }
      setModalViewer({ studyId, seriesId });
      return;
    }
    if (mode === 'multi-tab') {
      const ackd = await sendAddStudy(studyId, seriesId);
      if (ackd) return;
    }
    window.open(viewerUrl(studyId, seriesId), '_blank');
  }

  async function toggleExpandStudy(studyId: number) {
    if (expandedStudyId === studyId) {
      setExpandedStudyId(null);
      setExpandedSeries([]);
      return;
    }

    setExpandedStudyId(studyId);
    setExpandedSeries([]);
    setLoadingSeries(true);
    try {
      const res = await apiCall<any>('study.get', { studyId });
      if (res.s === 'ok' && res.data?.[0]?.series) {
        const sorted = [...res.data[0].series].sort(
          (a: any, b: any) => (parseInt(a.seriesNumber, 10) || 0) - (parseInt(b.seriesNumber, 10) || 0)
        );
        setExpandedSeries(sorted);
      }
    } catch { /* ignore */ }
    finally {
      setLoadingSeries(false);
    }
  }

  function handleLogout() {
    logout();
    navigate('/login', { replace: true });
  }

  // Click handling — single click expands, double click opens viewer
  const clickTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  function handleRowClick(studyId: number) {
    if (clickTimerRef.current) {
      // Double click detected
      clearTimeout(clickTimerRef.current);
      clickTimerRef.current = null;
      openStudy(studyId);
    } else {
      // Wait to see if a second click comes
      clickTimerRef.current = setTimeout(() => {
        clickTimerRef.current = null;
        toggleExpandStudy(studyId);
      }, 300);
    }
  }

  function handleTouchStart(studyId: number) {
    longPressRef.current = setTimeout(() => {
      longPressRef.current = null;
      openStudy(studyId);
    }, 600);
  }

  function handleTouchEnd() {
    if (longPressRef.current) {
      clearTimeout(longPressRef.current);
      longPressRef.current = null;
    }
  }

  // Column management
  function toggleColumn(key: string) {
    setColumns(columns.map((c) => (c.key === key ? { ...c, visible: !c.visible } : c)));
  }

  function onResizeStart(key: string, e: React.MouseEvent) {
    e.preventDefault();
    const col = columns.find((c) => c.key === key);
    if (!col) return;
    resizingCol.current = { key, startX: e.clientX, startW: col.width };

    const onMove = (ev: MouseEvent) => {
      if (!resizingCol.current) return;
      const diff = ev.clientX - resizingCol.current.startX;
      const newWidth = Math.max(40, resizingCol.current.startW + diff);
      setColumns(
        columns.map((c) =>
          c.key === resizingCol.current!.key ? { ...c, width: newWidth } : c
        )
      );
    };
    const onUp = () => {
      resizingCol.current = null;
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }

  function onColumnDragOver(e: React.DragEvent, targetKey: string) {
    e.preventDefault();
    if (!dragColKey || dragColKey === targetKey) return;
    const fromIdx = columns.findIndex((c) => c.key === dragColKey);
    const toIdx = columns.findIndex((c) => c.key === targetKey);
    if (fromIdx === -1 || toIdx === -1) return;
    const reordered = [...columns];
    const [moved] = reordered.splice(fromIdx, 1);
    reordered.splice(toIdx, 0, moved);
    setColumns(reordered);
  }

  // Runtime filter: strip the aiTriage column when no plugins fill the
  // worklist_row_badge slot. The default-false visibility handles fresh
  // installs; this handles pilots whose persisted state has it set to
  // true but had their plugins turned off / allowlist cleared later.
  const badgePlugins = usePluginsForSlot('worklist_row_badge');
  const visibleCols = columns.filter(
    (c) => c.visible && !(c.key === 'aiTriage' && badgePlugins.length === 0),
  );

  // Client-side local search — filters already-loaded studies by matching
  // the search term against all visible column values. Then applies the
  // current sort if any.
  const displayedStudies = useMemo(() => {
    let rows: Study[] = studies;
    if (localSearch.trim()) {
      const term = localSearch.toLowerCase();
      rows = rows.filter((study) =>
        visibleCols.some((col) => {
          const val = (study as any)[col.key];
          return val != null && String(val).toLowerCase().includes(term);
        })
      );
    }
    if (sort) {
      // Numeric columns compare as numbers; everything else as
      // case-insensitive string. Null/undefined sinks to the bottom in
      // both directions so empty cells don't poison the top of the list.
      const numericKeys = new Set(['numImages', 'studyId']);
      const dir = sort.dir === 'asc' ? 1 : -1;
      rows = [...rows].sort((a, b) => {
        const av = (a as any)[sort.col];
        const bv = (b as any)[sort.col];
        const ae = av === null || av === undefined || av === '';
        const be = bv === null || bv === undefined || bv === '';
        if (ae && be) return 0;
        if (ae) return 1;
        if (be) return -1;
        if (numericKeys.has(sort.col)) {
          return (Number(av) - Number(bv)) * dir;
        }
        return String(av).toLowerCase().localeCompare(String(bv).toLowerCase()) * dir;
      });
    }
    return rows;
  }, [studies, localSearch, visibleCols, sort]);

  // Fire the plugin "on_worklist_view" trigger — debounced 2s, cached per
  // (plugin, study) so repeat filters don't re-call the vendor. Lives at
  // this level so any filter change or sort flows into the hook via a
  // stable list of study ids.
  useWorklistViewTrigger(
    useMemo(() => displayedStudies.map(studyRowToFacts), [displayedStudies]),
  );

  return (
    <div className={styles.page}>
      {/* Header */}
      <header className={styles.header}>
        <img src={`${import.meta.env.BASE_URL}logo.png`} alt="EDGE PACS Viewer" className={styles.logo} />
        <div className={styles.headerRight}>
          <span className={styles.version} title="Build version — increments on every deploy">
            v{__APP_VERSION__}
          </span>
          <span className={styles.user}>{displayName}</span>
          <button
            className={styles.logoutBtn}
            onClick={() => setShowSettings(true)}
            title="Settings"
            style={{ padding: '4px 8px' }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="3" />
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33h0a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51h0a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82v0a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
            </svg>
          </button>
          <button className={styles.logoutBtn} onClick={handleLogout}>
            Logout
          </button>
        </div>
      </header>
      {showSettings && <SettingsDialog onClose={() => setShowSettings(false)} />}

      {/* Toolbar */}
      <div className={styles.toolbar}>
        {/* Saved filter selector */}
        <select
          className={styles.filterSelect}
          value={activeFilterId ?? ''}
          onChange={(e) => {
            const id = Number(e.target.value);
            if (!id) { clearFilters(); return; }
            const sf = savedFilters.find((f) => f.filterId === id);
            if (sf) applySavedFilter(sf);
          }}
        >
          <option value="">-- Select Filter --</option>
          {savedFilters.map((sf) => (
            <option key={sf.filterId} value={sf.filterId}>
              {sf.name}{sf.isDefault ? ' *' : ''}
            </option>
          ))}
        </select>
        <button
          className={styles.editFilterBtn}
          onClick={() => setShowManageFilters(true)}
          title="Add, edit or delete filters"
        >
          Filters
        </button>

        <div className={styles.toolbarDivider} />

        {/* Group: worklist search — inline search + custom + clear together
             so it's obvious they act on the SAME list, not the selection. */}
        <input
          className={styles.localSearchInput}
          placeholder="Search worklist..."
          value={localSearch}
          onChange={(e) => setLocalSearch(e.target.value)}
        />
        <button onClick={() => setShowCustomSearch(true)}>Custom Search</button>
        <button onClick={clearFilters}>Clear</button>

        <div className={styles.toolbarDivider} />

        {/* Group: selection actions — Open / Send / Burn. All disabled
             until at least one study is selected via the row checkboxes.
             Send is single-study only (uses the first selected). */}
        <button
          disabled={selectedCount === 0}
          onClick={handleOpenSelected}
          title="Open selected study in the viewer"
        >
          Open{selectedCount > 0 ? ` (${selectedCount})` : ''}
        </button>
        <button
          disabled={selectedCount !== 1}
          onClick={handleSendSelected}
          title={
            selectedCount === 0
              ? 'Select one study to send'
              : selectedCount > 1
                ? 'Send accepts only one study at a time — clear extras'
                : 'Send selected study to the send dialog (PACS, DICOM Send, etc.)'
          }
        >
          Send
        </button>
        {canBurnMedia && (
          <button
            disabled={selectedCount === 0}
            onClick={handleBurnSelected}
            title="Burn selected study/studies to media"
          >
            💿 Burn{selectedCount > 0 ? ` (${selectedCount})` : ''}
          </button>
        )}

        {/* Plugin manual bulk-send: only renders if at least one triage
            plugin is installed, so the button is invisible on vanilla
            deployments with no AI configured. */}
        <ManualBulkSendButton selectedStudyIds={[...selectedStudyIds].map(String)} />

        <div className={styles.spacer} />
        <button
          ref={columnsBtnRef}
          className={styles.columnsBtn}
          onClick={() => setShowColumnConfig(!showColumnConfig)}
        >
          Columns
        </button>
      </div>

      {/* Column Config Dropdown */}
      {showColumnConfig && (
        <div ref={columnConfigRef} className={styles.columnConfig}>
          <div className={styles.columnConfigHeader}>
            <strong>Visible Columns</strong>
            <button onClick={resetColumns} className={styles.resetBtn}>
              Reset
            </button>
          </div>
          {columns.map((col) => (
            <label key={col.key} className={styles.colCheckbox}>
              <input
                type="checkbox"
                checked={col.visible}
                onChange={() => toggleColumn(col.key)}
              />
              {col.label}
            </label>
          ))}
        </div>
      )}

      {/* Custom Search Dialog */}
      {showCustomSearch && (
        <div className={styles.dialogOverlay} onClick={() => setShowCustomSearch(false)}>
          <div className={styles.customSearchDialog} onClick={(e) => e.stopPropagation()}>
            <h3>Custom Search</h3>
            <div className={styles.searchGrid}>
              <label>
                Patient Name
                <input
                  placeholder="Last, First, or part of either"
                  value={draft.patientName || ''}
                  onChange={(e) => setDraft({ ...draft, patientName: e.target.value })}
                  onKeyDown={(e) => e.key === 'Enter' && applyCustomSearch()}
                />
              </label>
              <label>
                Patient ID
                <input
                  value={draft.patientId || ''}
                  onChange={(e) => setDraft({ ...draft, patientId: e.target.value })}
                  onKeyDown={(e) => e.key === 'Enter' && applyCustomSearch()}
                />
              </label>
              <label>
                Accession #
                <input
                  value={draft.accessionNumber || ''}
                  onChange={(e) => setDraft({ ...draft, accessionNumber: e.target.value })}
                  onKeyDown={(e) => e.key === 'Enter' && applyCustomSearch()}
                />
              </label>
              <label>
                Modality
                <select
                  value={draft.modality || ''}
                  onChange={(e) => setDraft({ ...draft, modality: e.target.value })}
                >
                  <option value="">All</option>
                  <option value="CR">CR</option>
                  <option value="CT">CT</option>
                  <option value="MR">MR</option>
                  <option value="US">US</option>
                  <option value="XA">XA</option>
                  <option value="DX">DX</option>
                  <option value="MG">MG</option>
                  <option value="PT">PT</option>
                  <option value="NM">NM</option>
                </select>
              </label>
              <label>
                Date Range
                <select
                  value={draft.studyDateSpan ?? 0}
                  onChange={(e) => setDraft({ ...draft, studyDateSpan: Number(e.target.value) })}
                >
                  {DATE_SPAN_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </label>
              {(!draft.studyDateSpan || draft.studyDateSpan === 0) && (
                <>
                  <label>
                    From Date
                    <input
                      type="date"
                      value={draft.studyDateFrom || ''}
                      onChange={(e) => setDraft({ ...draft, studyDateFrom: e.target.value })}
                    />
                  </label>
                  <label>
                    To Date
                    <input
                      type="date"
                      value={draft.studyDateTo || ''}
                      onChange={(e) => setDraft({ ...draft, studyDateTo: e.target.value })}
                    />
                  </label>
                </>
              )}
              <label>
                Ref. Physician
                <input
                  value={draft.referringPhysician || ''}
                  onChange={(e) => setDraft({ ...draft, referringPhysician: e.target.value })}
                  onKeyDown={(e) => e.key === 'Enter' && applyCustomSearch()}
                />
              </label>
              <label>
                Study Description
                <input
                  value={draft.studyDescription || ''}
                  onChange={(e) => setDraft({ ...draft, studyDescription: e.target.value })}
                  onKeyDown={(e) => e.key === 'Enter' && applyCustomSearch()}
                />
              </label>
              <label>
                Institution
                <input
                  value={draft.institution || ''}
                  onChange={(e) => setDraft({ ...draft, institution: e.target.value })}
                  onKeyDown={(e) => e.key === 'Enter' && applyCustomSearch()}
                />
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <input
                  type="checkbox"
                  checked={!!draft.keyImagesOnly}
                  onChange={(e) => setDraft({ ...draft, keyImagesOnly: e.target.checked })}
                />
                Only studies with key images
              </label>
            </div>
            <div className={styles.dialogActions}>
              <button
                onClick={() => setDraft({})}
                className={styles.cancelBtn}
                title="Clear every field in the Custom Search form. Useful when a saved filter's fields (e.g. modality=MG) have carried over and are silently narrowing your search."
              >
                Reset Fields
              </button>
              <button onClick={() => { setShowCustomSearch(false); openSaveDialog(); }}>Save As Filter</button>
              <div className={styles.spacer} />
              <button onClick={() => setShowCustomSearch(false)} className={styles.cancelBtn}>
                Cancel
              </button>
              <button onClick={applyCustomSearch}>Search</button>
            </div>
          </div>
        </div>
      )}

      {/* Save/Edit Filter Dialog — z-index bumped so it layers above Manage
          Filters when both are open (Manage stays mounted in the background
          so Cancel returns to it). */}
      {showSaveDialog && (
        <div
          className={styles.dialogOverlay}
          style={{ zIndex: 600 }}
          onClick={() => setShowSaveDialog(false)}
        >
          <div className={styles.customSearchDialog} onClick={(e) => e.stopPropagation()}>
            <h3>{editingFilter ? 'Edit Filter' : 'Save Filter'}</h3>
            <div className={styles.searchGrid}>
              <label>
                Filter Name *
                <input
                  value={filterName}
                  onChange={(e) => setFilterName(e.target.value)}
                  autoFocus
                />
              </label>
              <label className={styles.defaultCheckLabel}>
                <input
                  type="checkbox"
                  checked={filterIsDefault}
                  onChange={(e) => setFilterIsDefault(e.target.checked)}
                />
                Set as default filter
              </label>
              <label>
                Patient Name
                <input
                  value={draft.patientName || ''}
                  onChange={(e) => setDraft({ ...draft, patientName: e.target.value })}
                />
              </label>
              <label>
                Patient ID
                <input
                  value={draft.patientId || ''}
                  onChange={(e) => setDraft({ ...draft, patientId: e.target.value })}
                />
              </label>
              <label>
                Accession #
                <input
                  value={draft.accessionNumber || ''}
                  onChange={(e) => setDraft({ ...draft, accessionNumber: e.target.value })}
                />
              </label>
              <label>
                Modality
                <select
                  value={draft.modality || ''}
                  onChange={(e) => setDraft({ ...draft, modality: e.target.value })}
                >
                  <option value="">All</option>
                  <option value="CR">CR</option>
                  <option value="CT">CT</option>
                  <option value="MR">MR</option>
                  <option value="US">US</option>
                  <option value="XA">XA</option>
                  <option value="DX">DX</option>
                  <option value="MG">MG</option>
                  <option value="PT">PT</option>
                  <option value="NM">NM</option>
                </select>
              </label>
              <label>
                Date Range
                <select
                  value={draft.studyDateSpan ?? 0}
                  onChange={(e) => setDraft({ ...draft, studyDateSpan: Number(e.target.value) })}
                >
                  {DATE_SPAN_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </label>
              <label>
                Ref. Physician
                <input
                  value={draft.referringPhysician || ''}
                  onChange={(e) => setDraft({ ...draft, referringPhysician: e.target.value })}
                />
              </label>
              <label>
                Study Description
                <input
                  value={draft.studyDescription || ''}
                  onChange={(e) => setDraft({ ...draft, studyDescription: e.target.value })}
                />
              </label>
              <label>
                Institution
                <input
                  value={draft.institution || ''}
                  onChange={(e) => setDraft({ ...draft, institution: e.target.value })}
                />
              </label>
            </div>
            <div className={styles.dialogActions}>
              {editingFilter && (
                <button
                  className={styles.deleteBtn}
                  onClick={() => {
                    handleDeleteFilter(editingFilter.filterId);
                    setShowSaveDialog(false);
                  }}
                >
                  Delete
                </button>
              )}
              <div className={styles.spacer} />
              <button onClick={() => setShowSaveDialog(false)} className={styles.cancelBtn}>
                Cancel
              </button>
              <button onClick={handleSaveFilter}>
                {editingFilter ? 'Update' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Manage Filters Dialog */}
      {showManageFilters && (
        <div className={styles.dialogOverlay} onClick={() => setShowManageFilters(false)}>
          <div className={styles.manageDialog} onClick={(e) => e.stopPropagation()}>
            <div className={styles.manageHeader}>
              <h3>Manage Filters</h3>
              <button onClick={() => openSaveDialog()}>
                + New Filter
              </button>
            </div>
            {savedFilters.length === 0 ? (
              <div className={styles.manageEmpty}>
                No saved filters. Click "+ New Filter" to create one.
              </div>
            ) : (
              <div className={styles.manageList}>
                {savedFilters.map((sf) => (
                  <div key={sf.filterId} className={styles.manageRow}>
                    <div className={styles.manageInfo}>
                      <span className={styles.manageName}>
                        {sf.name}
                        {sf.isDefault && <span className={styles.manageDefault}> (default)</span>}
                      </span>
                      <span className={styles.manageDetail}>
                        {[
                          sf.modality,
                          sf.patientName,
                          sf.studyDescription,
                          sf.institution,
                          sf.studyDateSpan > 0 && sf.studyDateSpan !== 254
                            ? DATE_SPAN_OPTIONS.find((o) => o.value === sf.studyDateSpan)?.label
                            : null,
                        ].filter(Boolean).join(' / ') || 'No criteria'}
                      </span>
                    </div>
                    <div className={styles.manageActions}>
                      <button
                        className={styles.manageApplyBtn}
                        onClick={() => { applySavedFilter(sf); setShowManageFilters(false); }}
                      >
                        Apply
                      </button>
                      <button
                        className={styles.editFilterBtn}
                        onClick={() => openSaveDialog(sf)}
                      >
                        Edit
                      </button>
                      <button
                        className={styles.deleteBtn}
                        onClick={() => handleDeleteFilter(sf.filterId)}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className={styles.dialogActions}>
              <div className={styles.spacer} />
              <button onClick={() => setShowManageFilters(false)} className={styles.cancelBtn}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error */}
      {error && <div className={styles.error}>{error}</div>}

      {/* Desktop Table View */}
      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
            <tr>
              {visibleCols.map((col, ci) => {
                const isSorted = sort?.col === col.key;
                const sortIndicator = isSorted ? (sort!.dir === 'asc' ? ' ▲' : ' ▼') : '';
                // Tri-state select-all lives in the FIRST visible column header,
                // mirroring where the row checkbox sits (ci === 0 in the body).
                const isFirst = ci === 0;
                const visibleIds = isFirst ? displayedStudies.map((s) => s.studyId) : [];
                const allChecked = isFirst && visibleIds.length > 0 && visibleIds.every((id) => selectedStudyIds.has(id));
                const someChecked = isFirst && !allChecked && visibleIds.some((id) => selectedStudyIds.has(id));
                return (
                  <th
                    key={col.key}
                    style={{ width: col.width, minWidth: col.width }}
                    draggable
                    onDragStart={() => setDragColKey(col.key)}
                    onDragOver={(e) => onColumnDragOver(e, col.key)}
                    onDragEnd={() => setDragColKey(null)}
                    onClick={() => cycleSort(col.key)}
                    className={dragColKey === col.key ? styles.draggingTh : ''}
                    title={`Click to sort by ${col.label}`}
                  >
                    <div className={styles.thInner}>
                      {isFirst && (
                        <input
                          type="checkbox"
                          checked={allChecked}
                          ref={(el) => { if (el) el.indeterminate = someChecked; }}
                          onClick={(e) => e.stopPropagation()}
                          onChange={toggleSelectAll}
                          disabled={visibleIds.length === 0}
                          title={allChecked ? 'Deselect all' : 'Select all visible studies'}
                          style={{ marginRight: 6, cursor: 'pointer' }}
                        />
                      )}
                      <span>{col.label}{sortIndicator}</span>
                      <div
                        className={styles.resizeHandle}
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => onResizeStart(col.key, e)}
                      />
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={visibleCols.length} className={styles.center}>
                  Searching...
                </td>
              </tr>
            ) : displayedStudies.length === 0 ? (
              <tr>
                <td colSpan={visibleCols.length} className={styles.center}>
                  {studies.length === 0
                    ? 'No studies found. Select a filter or use Custom Search.'
                    : 'No matches for local search.'}
                </td>
              </tr>
            ) : (
              displayedStudies.map((study) => (
                <React.Fragment key={study.studyId}>
                  <tr
                    onClick={() => handleRowClick(study.studyId)}
                    className={`${styles.row} ${expandedStudyId === study.studyId ? styles.rowExpanded : ''}`}
                  >
                    {visibleCols.map((col, ci) => (
                      <td key={col.key} style={{ width: col.width, minWidth: col.width }}>
                        {ci === 0 && (
                          <>
                            <input
                              type="checkbox"
                              checked={selectedStudyIds.has(study.studyId)}
                              onClick={(e) => e.stopPropagation()}
                              onChange={() => toggleSelected(study.studyId)}
                              title="Select this study for Open / Send / Burn"
                              style={{ marginRight: 6, cursor: 'pointer' }}
                            />
                            <span className={styles.expandIcon}>
                              {expandedStudyId === study.studyId ? '▼' : '▶'}
                            </span>
                          </>
                        )}
                        {col.key === 'reportStatus' ? (
                          <ReportBadge
                            status={(study.reportStatus as any) || 'none'}
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/report/${study.studyId}`, {
                                state: { from: '/worklist', fromLabel: 'Worklist' },
                              });
                            }}
                          />
                        ) : col.key === 'aiTriage' ? (
                          <WorklistPluginColumn studyId={String(study.studyId)} />
                        ) : (
                          (study as any)[col.key] ?? ''
                        )}
                      </td>
                    ))}
                  </tr>
                  {expandedStudyId === study.studyId && (
                    <tr className={styles.seriesRow}>
                      <td colSpan={visibleCols.length} className={styles.seriesCell}>
                        {loadingSeries ? (
                          <div className={styles.seriesLoading}>Loading series...</div>
                        ) : expandedSeries.length === 0 ? (
                          <div className={styles.seriesLoading}>No series found</div>
                        ) : (
                          <div className={styles.seriesList}>
                            {expandedSeries.map((s) => (
                              <div
                                key={s.seriesId}
                                className={styles.seriesCard}
                                onDoubleClick={(e) => {
                                  e.stopPropagation();
                                  openStudyWithSeries(study.studyId, s.seriesId);
                                }}
                                title="Double-click to open this series"
                              >
                                <div className={styles.seriesCardThumb}>
                                  {s.modality}
                                </div>
                                <div className={styles.seriesCardInfo}>
                                  <div className={styles.seriesCardDesc}>
                                    {s.seriesDescription || `Series ${s.seriesNumber}`}
                                  </div>
                                  <div className={styles.seriesCardMeta}>
                                    {s.modality} &middot; {s.numImages} img
                                    {s.bodyPart ? ` · ${s.bodyPart}` : ''}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile Card View */}
      <div className={styles.mobileCards}>
        {loading ? (
          <div className={styles.mobileEmpty}>Searching...</div>
        ) : displayedStudies.length === 0 ? (
          <div className={styles.mobileEmpty}>
            {studies.length === 0
              ? 'No studies found. Select a filter or use Custom Search.'
              : 'No matches for local search.'}
          </div>
        ) : (
          displayedStudies.map((study) => (
            <div
              key={study.studyId}
              className={styles.mobileCard}
              onClick={() => openStudy(study.studyId)}
              onTouchStart={() => handleTouchStart(study.studyId)}
              onTouchEnd={handleTouchEnd}
              onTouchCancel={handleTouchEnd}
            >
              <div className={styles.mobileCardHeader}>
                <span className={styles.mobileCardName}>{study.patientName}</span>
                <span className={styles.mobileCardModality}>{study.modality}</span>
              </div>
              <div className={styles.mobileCardBody}>
                <span>ID: {study.patientId}</span>
                <span>{study.studyDate}</span>
              </div>
              <div className={styles.mobileCardBody}>
                <span>{study.studyDescription}</span>
                <span>{study.numImages} img</span>
              </div>
              {study.referringPhysician && (
                <div className={styles.mobileCardFooter}>
                  Ref: {study.referringPhysician}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Jobs Panel */}
      <JobsPanel
        refreshTrigger={jobRefreshTrigger}
        onJobCreated={() => setJobRefreshTrigger((n) => n + 1)}
      />

      {/* Status Bar */}
      <div className={styles.statusBar}>
        {localSearch
          ? `${displayedStudies.length} of ${studies.length} studies`
          : `${studies.length} studies`}
      </div>

      {/* Send Dialog */}
      {sendStudy && (
        <SendDialog
          studyId={sendStudy.studyId}
          studyDescription={sendStudy.studyDescription}
          patientName={sendStudy.patientName}
          onClose={() => setSendStudy(null)}
          onJobCreated={() => setJobRefreshTrigger((n) => n + 1)}
        />
      )}

      {/* Modal viewer (viewerOpenMode='modal'). Full-window overlay; closing
          drops back to the worklist with all state intact (search, scroll,
          column config, etc.). Esc also closes via the keydown effect. */}
      {modalViewer && (
        <div className={styles.viewerModal}>
          <Viewer
            studyId={modalViewer.studyId}
            requestedSeriesId={modalViewer.seriesId}
            onClose={() => setModalViewer(null)}
            embedded
          />
        </div>
      )}

      {/* Install-Companion dialog. Only rendered when launchCompanion()
          detected the protocol handler didn't fire (no window blur within
          its timeout). On machines with the Companion installed, this
          never appears. */}
      {launchingMsg && (
        <CompanionLaunchDialog
          action={launchingMsg}
          installerUrl={getEdgePrintInstallerUrl(getServerUrl())}
          onClose={() => setLaunchingMsg(null)}
          onRetry={lastLaunchAction ?? undefined}
        />
      )}
    </div>
  );
}
